import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";


export default function AdminInterviewsTable({ interviews, onApprove }) {
  if (!interviews || interviews.length === 0) {
    return <Typography sx={{ p: 1 }}>No interviews.</Typography>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Interview ID</TableCell>
            <TableCell>Interviewee</TableCell>
            <TableCell>Topic</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Type Owner</TableCell>
            <TableCell>Type Org</TableCell>
            <TableCell>Created at</TableCell>
            <TableCell>Feedback</TableCell>
            <TableCell>Video status</TableCell>
            <TableCell>Transcript status</TableCell>
            <TableCell>Overall status</TableCell>
            <TableCell>Review</TableCell>
            <TableCell>Analysis</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {interviews.map((intv) => {
            const isApproved = intv.review_status === "approved";
            const isProcessing = intv.analysis_status === "processing";

            // Post-interview feedback
            const fb = intv.post_interview_feedback; // { rating, why, created_at }
            const fbRating = fb?.rating;
            const fbWhy = fb?.why;

            return (
              <TableRow key={intv.interview_id}>
                <TableCell>{intv.interview_id}</TableCell>
                <TableCell>{intv.interviewee_name}</TableCell>
                <TableCell>
                  {(() => {
                    const focus = String(intv.interview_focus_type || "product").toLowerCase();
                    const topic =
                      intv.interview_topic_name ||
                      intv.topic_name ||
                      intv.product_name ||
                      "—";

                    const label = focus === "jtbd" ? "JTBD" : "Product";
                    const scriptId = intv.interview_script_id || "";

                    return (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <div>{topic}</div>
                        <Typography variant="caption" color="text.secondary">
                          {label}
                          {scriptId ? ` • script: ${scriptId}` : ""}
                        </Typography>
                      </Box>
                    );
                  })()}
                </TableCell>

                <TableCell>{intv.interview_type_slug || ""}</TableCell>
                <TableCell>{intv.owner_email || "-"}</TableCell>
                <TableCell>{intv.organization_id || "-"}</TableCell>
                <TableCell>{intv.created_at}</TableCell>
                <TableCell>
                  {fbRating ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {fbRating}/10
                      </Typography>

                      {fbWhy ? (
                        <Tooltip title={fbWhy}>
                          <InfoOutlinedIcon fontSize="small" />
                        </Tooltip>
                      ) : null}
                    </Box>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>{intv.video_status}</TableCell>
                <TableCell>{intv.transcript_status}</TableCell>
                <TableCell>{intv.overall_status}</TableCell>
                <TableCell>{intv.review_status || "pending"}</TableCell>
                <TableCell>{intv.analysis_status || "not_started"}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Link to={`/interview/${intv.interview_id}`}>Open</Link>
                    <Link to={`/interviews/${intv.interview_id}/view`}>View</Link>

                    {onApprove && String(intv.interview_type_slug || "").trim() && (
                      <>
                        {/* If processing, allow a manual force restart (for stuck jobs) */}
                        {isProcessing ? (
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => {
                              const ok = window.confirm(
                                "This interview is currently marked as 'processing'.\n\n" +
                                  "Use Force Restart only if processing is stuck.\n" +
                                  "This will re-run analysis and overwrite the processed JSON output.\n\n" +
                                  "Force restart now?"
                              );
                              if (!ok) return;
                              onApprove(intv.interview_id, { forceReanalyze: true });
                            }}
                            sx={{ textTransform: "none", fontWeight: 700 }}
                          >
                            Force Restart Analysis
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant={isApproved ? "contained" : "outlined"}
                            color={isApproved ? "warning" : "primary"}
                            onClick={() =>
                              onApprove(intv.interview_id, { forceReanalyze: isApproved })
                            }
                            sx={{
                              textTransform: "none",
                              ...(isApproved ? { fontWeight: 700 } : {}),
                            }}
                          >
                            {isApproved ? "Re-analyze & Process" : "Approve & Analyze"}
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}